/**
 * The seam between `GameScene` and whatever is actually advancing the hand.
 *
 * Solo that is `BotDriver` plus the local match layer (`SoloDriver.ts`);
 * online it is the server, reached through `NetworkGameController`
 * (`OnlineDriver.ts`). The scene talks to `SceneDriver` and nothing else, so
 * the online path adds no `online ?` branch to the render loop, the tap
 * handler, the action bar, or the coach — which is what the plan's T13
 * rollback note asks for: solo cannot regress, because solo never takes a
 * different branch. `mount` picking a driver is the whole of the gate.
 *
 * Direction of travel: the scene calls a `SceneDriver` method when the human
 * acts, and the driver calls a `SceneDriverHooks` callback when the hand
 * moves. Both directions are shaped by what solo already needed — online
 * simply fills them from `STATE_SYNC` instead of from a local transition.
 */

import type { RoomClient } from '@bn-games/room-client';
import type { HandResult, MahjongState } from '../game/GameState.js';
import type { Claim, SeatClaims } from '../game/Claims.js';
import type { Seat } from '../game/types.js';
import type { Ruleset } from '../game/ruleset/types.js';
import type { KongDecision } from '../ai/types.js';
import type { MatchState } from '../match/MatchState.js';
import type { HandScore } from '../scoring/types.js';
import { verifiableDeal, type VerifiableDeal } from '../verify/verifyModel.js';
import type { ClientMessage, ServerMessage } from '../network/protocol.js';
import { NetworkGameController } from '../network/NetworkGameController.js';
import { createOnlineOverlayHost } from '../ui/OnlineOverlayHost.js';
import { SoloDriver } from './SoloDriver.js';
import { OnlineDriver } from './OnlineDriver.js';

/** Driver → scene. Every callback is a request to change what is on screen. */
export interface SceneDriverHooks {
  readonly getState: () => MahjongState;
  readonly setState: (next: MahjongState) => void;
  readonly getMatchState: () => MatchState | null;
  readonly setMatchState: (next: MatchState | null) => void;
  /** Local seat on turn to discard: `canDeclareTsumo` offers the win, `kongOptions` any kong. */
  readonly onLocalTurn: (canDeclareTsumo: boolean, kongOptions: readonly KongDecision[]) => void;
  /** The local seat may claim the open discard — surface its options. */
  readonly onLocalClaimWindow: (claims: SeatClaims) => void;
  /**
   * `handScore` is Classical-only: scored locally solo, carried by the snapshot
   * online. `deal` is the verifiable-shuffle bundle, and is online-only in a
   * stronger sense — solo has no proof to offer and never passes one, so the
   * hand-end modal's verify button is structurally unreachable off the wire.
   */
  readonly onHandEnd: (
    result: HandResult,
    handScore: HandScore | null,
    deal?: VerifiableDeal | null,
  ) => void;
  readonly clearLocalUi: () => void;
  /** A hand is live again — drop any open hand-end modal (online: another seat advanced the match). */
  readonly closeHandEnd: () => void;
}

/** Scene → driver. One method per thing the human can do at the table. */
export interface SceneDriver {
  /** The point of view every renderer rotates around (see `layout/types.ts`'s `seatPosition`). */
  readonly localSeat: Seat;
  /** Begin driving — called once, after the scene has mounted. */
  start(): void;
  discard(tileId: number): void;
  declareTsumo(): void;
  declareKong(kong: KongDecision): void;
  /** Resolve the open claim window — `null` is Skip. */
  resolveClaim(choice: Claim | null): void;
  resolveExposedKong(): void;
  nextHand(): void;
  /**
   * The pause menu opened or closed (`ui/PauseMenu.ts`). **Optional**, and
   * `OnlineDriver` deliberately omits it: an online table has no local clock to
   * stop — the server drives the bots and owns the claim-window deadline, and
   * three other people are sitting at it — so the menu is only ever a menu
   * there. Solo it genuinely freezes: the bot loop's timers stop mid-hand.
   */
  setPaused?(paused: boolean): void;
  dispose(): void;
}

/** Solo free play, and Classical solo match play — iteration 1 + 2, unchanged. */
export interface SoloMountOptions {
  readonly mode?: 'solo';
  readonly state: MahjongState;
  readonly ruleset: Ruleset;
  /** `null`/omitted for Simplified free play — no match wrapper. */
  readonly matchState?: MatchState | null;
}

/**
 * An online table. Deliberately carries no `state` and no `matchState`: the
 * server is authoritative, so the first `STATE_SYNC` supplies both (the scene
 * simply paints nothing until one arrives), and the ruleset rides along on it
 * as `rulesetId`.
 */
export interface OnlineMountOptions {
  readonly mode: 'online';
  readonly room: RoomClient<ClientMessage, ServerMessage>;
  /** The table is finished — a concluded Classical match, or a Simplified
   *  room's final hand. The overlay shows it; this is for the caller's own
   *  bookkeeping. */
  readonly onGameOver: (
    winnerSeats: readonly Seat[],
    finalScores: readonly number[] | null,
  ) => void;
  /** The player left the table from the overlay — back to the menu. */
  readonly onLeaveTable: () => void;
}

export type MountOptions = SoloMountOptions | OnlineMountOptions;

export function isOnlineMount(opts: MountOptions): opts is OnlineMountOptions {
  return opts.mode === 'online';
}

/** The one place solo and online diverge. */
export function createSceneDriver(
  opts: MountOptions,
  hooks: SceneDriverHooks,
  onMatchComplete: () => void,
): SceneDriver {
  if (isOnlineMount(opts)) return createOnlineDriver(opts, hooks);
  return new SoloDriver({ hooks, ruleset: opts.ruleset, onMatchComplete });
}

/**
 * The online driver, its transport, and the DOM overlay that sits over both.
 * The transport is captured on the way past rather than built up front because
 * `OnlineDriver` owns when to connect; the overlay's buttons only ever fire
 * long after that, so a `let` is enough to close the loop.
 *
 * Two things are read off the snapshot stream here rather than inside the
 * driver, because both are about the *room* rather than about the hand the
 * driver is deciding on: whether a Classical match is still running, and
 * whether the deal that just ended can be verified. The latter also needs the
 * room code, which only the concrete transport has.
 */
function createOnlineDriver(opts: OnlineMountOptions, hooks: SceneDriverHooks): SceneDriver {
  let transport: NetworkGameController | null = null;
  // Read by the overlay's rematch guard — a running Classical match is what a
  // rematch would discard (see `ui/OnlineOverlayHost.ts`).
  let matchInProgress = false;
  // `null` until a hand ends in a verifiable room — see `verify/verifyModel.ts`.
  let deal: VerifiableDeal | null = null;
  // The hand-end modal is the only surface that offers verification, and it is
  // the driver that opens it, so the deal rides in on that same call.
  const dealAware: SceneDriverHooks = {
    ...hooks,
    onHandEnd: (result, handScore) => hooks.onHandEnd(result, handScore, deal),
  };
  const surface = createOnlineOverlayHost({
    requestRematch: () => transport?.overlay.requestRematch(),
    acceptRematch: () => transport?.overlay.acceptRematch(),
    declineRematch: () => transport?.overlay.declineRematch(),
    dismiss: () => transport?.overlay.dismissOverlay(),
    leave: () => opts.onLeaveTable(),
    isMatchInProgress: () => matchInProgress,
  });
  return new OnlineDriver({
    hooks: dealAware,
    surface,
    connect: (onState) => {
      transport = new NetworkGameController({
        room: opts.room,
        onState: (state, snapshot) => {
          const match = snapshot.matchState;
          matchInProgress = match !== null && !match.finished;
          deal = verifiableDeal(snapshot, transport?.roomCode ?? null);
          onState(state, snapshot);
        },
        onGameOver: opts.onGameOver,
      });
      return transport;
    },
  });
}
